import { createReducer } from 'src/helpers/reducer';
import type { IAgencyOption, INamedRef, IStateOption } from '../sharedTypes';
import actionTypes from './actionTypes';
import type { ILoadAgenciesPayload, ILoadUnitsPayload, IPageResult, IPickersState } from './types';

const initState: IPickersState = {
  agencies: [],
  agenciesNextPage: null,
  agenciesSearch: '',
  isLoadingAgencies: false,
  agenciesFailed: false,
  engines: [],
  isLoadingEngines: false,
  enginesFailed: false,
  units: [],
  unitsNextPage: null,
  unitsSearch: '',
  unitsScope: null,
  isLoadingUnits: false,
  unitsFailed: false,
  states: [],
  isLoadingStates: false,
  statesFailed: false,
};

function onAgenciesSuccess(state: IPickersState, { payload }: { payload: IPageResult<IAgencyOption> }): IPickersState {
  return {
    ...state,
    isLoadingAgencies: false,
    agenciesFailed: false,
    agencies: payload.append ? [...state.agencies, ...payload.records] : payload.records,
    agenciesNextPage: payload.nextPage,
  };
}

export default createReducer(initState, {
  // A first page closes the old cursor: the saga keeps only the latest request, so a load-more fired
  // meanwhile would cancel this page and append the next one onto the previous search's rows.
  [actionTypes.LOAD_AGENCIES_REQUEST]: (state: IPickersState, { payload }: { payload: ILoadAgenciesPayload }) => {
    const isFirstPage = (payload.page ?? 1) === 1;

    return {
      ...state,
      isLoadingAgencies: true,
      agenciesFailed: false,
      agenciesNextPage: isFirstPage ? null : state.agenciesNextPage,
      agenciesSearch: isFirstPage ? payload.search ?? '' : state.agenciesSearch,
    };
  },
  [actionTypes.LOAD_AGENCIES_SUCCESS]: onAgenciesSuccess,
  // A failed first page drops the list — stale rows would read as the answer. A failed later page keeps
  // what landed and its cursor, so scrolling on asks for the page again.
  [actionTypes.LOAD_AGENCIES_ERROR]: (state: IPickersState, { payload }: { payload: { append: boolean } }) => ({
    ...state,
    isLoadingAgencies: false,
    agenciesFailed: true,
    agencies: payload.append ? state.agencies : [],
    agenciesNextPage: payload.append ? state.agenciesNextPage : null,
    agenciesSearch: payload.append ? state.agenciesSearch : '',
  }),
  [actionTypes.LOAD_ENGINES_REQUEST]: (state: IPickersState) => ({
    ...state,
    isLoadingEngines: true,
    enginesFailed: false,
    // Dropped so the previous entity's engines are not offered while the new list is loading.
    engines: [],
  }),
  [actionTypes.LOAD_ENGINES_SUCCESS]: (state: IPickersState, { payload }: { payload: { records: INamedRef[] } }) => ({
    ...state,
    isLoadingEngines: false,
    enginesFailed: false,
    engines: payload.records,
  }),
  [actionTypes.LOAD_ENGINES_ERROR]: (state: IPickersState) => ({
    ...state,
    isLoadingEngines: false,
    enginesFailed: true,
    engines: [],
  }),
  [actionTypes.LOAD_UNITS_REQUEST]: (state: IPickersState, { payload }: { payload: ILoadUnitsPayload }) => {
    const unitsScope = `${payload.pricingEngineId}|${payload.legalEntityId ?? ''}`;
    // The picker is open while a page loads, so another engine's units are dropped at once; a search of the
    // same list keeps its rows until the new page lands. A first page closes the old cursor against a stale load-more.
    const rescoped = unitsScope !== state.unitsScope;
    const isFirstPage = (payload.page ?? 1) === 1;

    return {
      ...state,
      isLoadingUnits: true,
      unitsFailed: false,
      unitsScope,
      units: rescoped ? [] : state.units,
      unitsNextPage: rescoped || isFirstPage ? null : state.unitsNextPage,
      unitsSearch: isFirstPage ? payload.search ?? '' : state.unitsSearch,
    };
  },
  [actionTypes.LOAD_UNITS_SUCCESS]: (state: IPickersState, { payload }: { payload: IPageResult<INamedRef> }) => ({
    ...state,
    isLoadingUnits: false,
    unitsFailed: false,
    units: payload.append ? [...state.units, ...payload.records] : payload.records,
    unitsNextPage: payload.nextPage,
  }),
  [actionTypes.LOAD_UNITS_ERROR]: (state: IPickersState, { payload }: { payload: { append: boolean } }) => ({
    ...state,
    isLoadingUnits: false,
    unitsFailed: true,
    units: payload.append ? state.units : [],
    unitsNextPage: payload.append ? state.unitsNextPage : null,
    unitsSearch: payload.append ? state.unitsSearch : '',
  }),
  [actionTypes.LOAD_STATES_REQUEST]: (state: IPickersState) => ({
    ...state,
    isLoadingStates: true,
    statesFailed: false,
  }),
  [actionTypes.LOAD_STATES_SUCCESS]: (state: IPickersState, { payload }: { payload: { records: IStateOption[] } }) => ({
    ...state,
    isLoadingStates: false,
    statesFailed: false,
    states: payload.records,
  }),
  [actionTypes.LOAD_STATES_ERROR]: (state: IPickersState) => ({
    ...state,
    isLoadingStates: false,
    statesFailed: true,
    states: [],
  }),
  [actionTypes.CLEAR_STORE]: () => initState,
});
